import React, { useState, useRef, useContext } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'
import { Card } from 'primereact/card'
import { ProgressBar } from 'primereact/progressbar'
import { Checkbox } from 'primereact/checkbox'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import { useDropzone } from 'react-dropzone'
import MainContentLayout from '../Layout/MainContentLayout'
import server from '../../server'
import { AuthenticationContext } from '../../Auth/authentication.context'

const FIELD_DEFINITIONS = {
  upc: {
    label: 'UPC',
    required: true,
    description: 'Universal Product Code (required)'
  },
  drug_identification: {
    label: 'Drug Identification',
    required: false,
    description: 'DIN number or drug ID (required if PA Percentage is provided)'
  },
  product_description: {
    label: 'Product Description',
    required: false,
    description: 'Product name or description'
  },
  price: {
    label: 'Price',
    required: false,
    description: 'Product price',
    showInSingleColumnMode: true
  },
  pa_percentage: {
    label: 'PA Percentage',
    required: false,
    description: 'Provincial Allowance percentage'
  },
  molecule_name: {
    label: 'Molecule Name',
    required: false,
    description: 'Molecule or drug name'
  },
  strength: {
    label: 'Strength',
    required: false,
    description: 'Drug strength or dosage (e.g., 10mg, 5ml)'
  },
  province: {
    label: 'Province',
    required: false,
    description: 'Province code (e.g., ON, BC, AB)',
    showInSingleColumnMode: true
  },
  // Province price fields for separate columns mode
  price_bc: {
    label: 'BC Price',
    required: false,
    description: 'British Columbia price',
    showInSeparateColumnsMode: true
  },
  price_ab: {
    label: 'AB Price',
    required: false,
    description: 'Alberta price',
    showInSeparateColumnsMode: true
  },
  price_sk: {
    label: 'SK Price',
    required: false,
    description: 'Saskatchewan price',
    showInSeparateColumnsMode: true
  },
  price_mb: {
    label: 'MB Price',
    required: false,
    description: 'Manitoba price',
    showInSeparateColumnsMode: true
  },
  price_on: {
    label: 'ON Price (National)',
    required: false,
    description: 'Ontario price (used as national pricing)',
    showInSeparateColumnsMode: true
  },
  price_qc: {
    label: 'QC Price',
    required: false,
    description: 'Quebec price',
    showInSeparateColumnsMode: true
  },
  price_nb: {
    label: 'NB Price',
    required: false,
    description: 'New Brunswick price',
    showInSeparateColumnsMode: true
  },
  price_pe: {
    label: 'PE Price',
    required: false,
    description: 'Prince Edward Island price',
    showInSeparateColumnsMode: true
  },
  price_ns: {
    label: 'NS Price',
    required: false,
    description: 'Nova Scotia price',
    showInSeparateColumnsMode: true
  },
  price_nt: {
    label: 'NT Price',
    required: false,
    description: 'Northwest Territories price',
    showInSeparateColumnsMode: true
  },
  price_nf: {
    label: 'NF/NL Price',
    required: false,
    description: 'Newfoundland and Labrador price',
    showInSeparateColumnsMode: true
  },
  price_yt: {
    label: 'YT Price',
    required: false,
    description: 'Yukon price',
    showInSeparateColumnsMode: true
  },
  price_nu: {
    label: 'NU Price',
    required: false,
    description: 'Nunavut price',
    showInSeparateColumnsMode: true
  }
}

export default function CatalogueImportWizard() {
  const [currentStep, setCurrentStep] = useState(1)
  const [file, setFile] = useState(null)
  const [manufacturer, setManufacturer] = useState(null)
  const [manufacturerOptions, setManufacturerOptions] = useState([])
  const [detectedColumns, setDetectedColumns] = useState([])
  const [columnMapping, setColumnMapping] = useState({})
  const [sampleRows, setSampleRows] = useState([])
  const [separator, setSeparator] = useState(null)
  const [loading, setLoading] = useState(false)
  const [detecting, setDetecting] = useState(false)
  const [priceFormatMode, setPriceFormatMode] = useState('single') // 'single' or 'separate'
  const [discontinueMissing, setDiscontinueMissing] = useState(true)
  const toast = useRef(null)
  const { user } = useContext(AuthenticationContext)

  const separatorOptions = [
    { name: 'Comma', code: ',', symbol: ',' },
    { name: 'Tab', code: '\t', symbol: '\t' },
    { name: 'Semicolon', code: ';', symbol: ';' },
    { name: 'Vertical Line', code: '|', symbol: '|' }
  ]

  const FileDropzone = ({ file, setFile, toast, disabled }) => {
    const removeFile = (e) => {
      e.stopPropagation()
      setFile(null)
      setDetectedColumns([])
      setColumnMapping({})
      setSampleRows([])
      setCurrentStep(1)
    }

    const { getRootProps, getInputProps } = useDropzone({
      accept: {
        'text/csv': ['.csv'],
        'text/plain': ['.txt'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.ms-excel': ['.xls']
      },
      disabled: disabled,
      onDrop: (acceptedFiles) => {
        const selectedFile = acceptedFiles[0]
        if (selectedFile) {
          setFile(selectedFile)
          toast.current.show({
            severity: 'success',
            summary: 'File Selected',
            detail: `${selectedFile.name} has been selected`,
            life: 3000
          })
        }
      }
    })

    return (
      <div
        {...getRootProps({
          className: `dropzone rounded p-3 hover-effect ${disabled ? 'opacity-50' : ''}`,
          style: { cursor: disabled ? 'not-allowed' : 'pointer' }
        })}
      >
        <input {...getInputProps()} disabled={disabled} />
        {file ? (
          <div className='d-flex justify-content-between align-items-center'>
            <div className='d-flex align-items-center'>
              <i
                className={`pi ${
                  file.type.includes('image')
                    ? 'pi-image'
                    : file.type === 'text/plain' || file.name.endsWith('.csv')
                    ? 'pi-file-o'
                    : file.name.endsWith('.xlsx') || file.name.endsWith('.xls')
                    ? 'pi-file-excel'
                    : 'pi-file-pdf'
                } mr-2`}
                style={{ fontSize: '1.5rem', color: '#3B82F6' }}
              ></i>
              <span className='font-medium'>{file.name}</span>
            </div>
            <Button
              icon='pi pi-times'
              className='p-button-rounded p-button-danger p-button-text'
              onClick={removeFile}
              tooltip='Remove file'
              tooltipOptions={{ position: 'top' }}
              disabled={disabled}
            />
          </div>
        ) : (
          <div className='d-flex flex-column align-items-center justify-content-center py-4'>
            <i
              className='pi pi-cloud-upload'
              style={{
                fontSize: '2.5rem',
                color: '#3B82F6',
                marginBottom: '1rem'
              }}
            ></i>
            <p className='m-0 text-center'>
              <span className='font-bold'>Drag and drop files here</span> <br />
              or click to select files
            </p>
            <p className='text-muted small mt-2'>
              Supported formats: CSV, TXT, XLSX, XLS
            </p>
          </div>
        )}
      </div>
    )
  }

  const getManufacturers = () => {
    const endpoint = user?.role === 'manufacturer' 
      ? '/manufacturers/user/manufacturers'
      : '/manufacturers'

    server
      .get(endpoint, {
        params: user?.role !== 'manufacturer' ? { forCatalogueImport: true } : undefined,
      })
      .then((response) => {
        const manufacturers = user?.role === 'manufacturer'
          ? response.data?.manufacturers || []
          : response.data?.Manufacturers || []

        const formattedManufacturers = manufacturers.map((m) => ({
          name: m?.manufacturer?.name || m?.name,
          code: m?.manufacturer?.id || m?.id
        }))

        setManufacturerOptions(formattedManufacturers)
      })
      .catch((error) => {
        console.error('Error fetching manufacturers:', error)
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load manufacturers',
          life: 3000
        })
      })
  }

  React.useEffect(() => {
    getManufacturers()
  }, [])

  const handleDetectColumns = async () => {
    if (!file) {
      toast.current.show({
        severity: 'error',
        summary: 'File Required',
        detail: 'Please upload a file first',
        life: 3000
      })
      return
    }

    if (!manufacturer) {
      toast.current.show({
        severity: 'error',
        summary: 'Manufacturer Required',
        detail: 'Please select a manufacturer first',
        life: 3000
      })
      return
    }

    setDetecting(true)
    const formData = new FormData()
    formData.append('file', file)
    
    // Add separator for TXT files
    if (file.type === 'text/plain' && separator) {
      formData.append('separator', separator.code || separator)
    } else if (file.type === 'text/plain' && !separator) {
      toast.current.show({
        severity: 'error',
        summary: 'Separator Required',
        detail: 'Please select a separator for TXT files',
        life: 3000
      })
      setDetecting(false)
      return
    }

    try {
      const response = await server.post('/uploads/pharmacy/detect-columns', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        setDetectedColumns(response.data.columns)
        setSampleRows(response.data.sampleRows || [])
        setCurrentStep(2)
        
        // Auto-map columns based on suggestions
        const autoMapping = {}
        response.data.columns.forEach((col) => {
          if (col.suggestedType && col.confidence > 0.5) {
            // Only auto-map if not already mapped (prefer higher confidence)
            if (autoMapping[col.suggestedType] === undefined) {
              autoMapping[col.suggestedType] = col.index
            } else {
              // If already mapped, check if this one has higher confidence
              const existingCol = response.data.columns.find(c => c.index === autoMapping[col.suggestedType])
              if (existingCol && col.confidence > (existingCol.confidence || 0)) {
                autoMapping[col.suggestedType] = col.index
              }
            }
          }
        })
        setColumnMapping(autoMapping)
        
        // Auto-detect format mode: if we found province price columns, suggest separate mode
        const hasProvincePriceColumns = Object.keys(autoMapping).some(key => key.startsWith('price_') && key !== 'price')
        if (hasProvincePriceColumns) {
          setPriceFormatMode('separate')
        }

        toast.current.show({
          severity: 'success',
          summary: 'Columns Detected',
          detail: `Found ${response.data.columns.length} columns`,
          life: 3000
        })
      } else {
        toast.current.show({
          severity: 'error',
          summary: 'Detection Failed',
          detail: response.data.message || 'Failed to detect columns',
          life: 3000
        })
      }
    } catch (error) {
      console.error('Error detecting columns:', error)
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to detect columns',
        life: 3000
      })
    } finally {
      setDetecting(false)
    }
  }

  const handleColumnMappingChange = (fieldKey, columnIndex) => {
    setColumnMapping({
      ...columnMapping,
      [fieldKey]: columnIndex === 'none' ? undefined : columnIndex
    })
  }

  const submitImport = async () => {
    setLoading(true)
    const formData = new FormData()
    formData.append('file', file)
    formData.append('manufacturer_name', manufacturer.name || manufacturer)
    formData.append('discontinue_missing', discontinueMissing ? 'true' : 'false')

    // Map column indices (backend expects 1-based, we store 0-based)
    Object.keys(columnMapping).forEach((fieldKey) => {
      if (columnMapping[fieldKey] !== undefined) {
        // Backend accepts both field_column and field formats, but we'll use field_column for consistency
        // For province prices, use price_bc_column format
        // For regular fields, use field_column format (e.g., upc_column, price_column)
        const fieldName = `${fieldKey}_column`
        formData.append(fieldName, (columnMapping[fieldKey] + 1).toString())
        // Also append without _column for backward compatibility
        formData.append(fieldKey, (columnMapping[fieldKey] + 1).toString())
      }
    })

    // Add separator for TXT files
    if (file.type === 'text/plain' && separator) {
      formData.append('separator', separator.code || separator)
    }

    try {
      await server.post('/uploads/pharmacy/product-catalogue', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Catalogue imported successfully',
        life: 3000
      })

      // Reset form
      setFile(null)
      setDetectedColumns([])
      setColumnMapping({})
      setSampleRows([])
      setCurrentStep(1)
      setManufacturer(null)
    } catch (error) {
      console.error('Error importing catalogue:', error)
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to import catalogue',
        life: 3000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    // Validate required fields
    if (!columnMapping.upc && columnMapping.upc !== 0) {
      toast.current.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'UPC column mapping is required',
        life: 3000
      })
      return
    }

    // If PA percentage is mapped, drug identification should also be mapped
    if (columnMapping.pa_percentage !== undefined && columnMapping.drug_identification === undefined) {
      toast.current.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Drug Identification is required when PA Percentage is provided',
        life: 3000
      })
      return
    }

    if (discontinueMissing) {
      confirmDialog({
        header: 'Confirm import',
        icon: 'pi pi-exclamation-triangle',
        message:
          'This import will discontinue (deactivate) any existing products for this manufacturer that are NOT included in this upload. Do you want to continue?',
        acceptLabel: 'Yes, continue',
        rejectLabel: 'Cancel',
        accept: submitImport
      })
      return
    }

    await submitImport()
  }

  const getColumnOptions = () => {
    return [
      { label: '-- Not Mapped --', value: 'none' },
      ...detectedColumns.map((col, index) => ({
        label: `${col.name} (Column ${index + 1})`,
        value: index
      }))
    ]
  }

  const renderStep1 = () => (
    <div>
      <h4 className='mb-4'>Step 1: Upload File</h4>
      <div className='mb-4'>
        <label htmlFor='manufacturer' className='font-bold d-block mb-2'>
          Manufacturer Name <span className='text-danger'>*</span>
        </label>
        <Dropdown
          id='manufacturer'
          value={manufacturer}
          onChange={(e) => setManufacturer(e.value)}
          options={manufacturerOptions}
          optionLabel='name'
          placeholder='Select a Manufacturer Name'
          className='w-100'
          filter
        />
      </div>
      <div className='mb-4'>
        <label htmlFor='fileUpload' className='font-bold d-block mb-2'>
          Upload File <span className='text-danger'>*</span>
        </label>
        <FileDropzone file={file} setFile={setFile} toast={toast} disabled={false} />
      </div>
      {file && file.type === 'text/plain' && (
        <div className='mb-4'>
          <label htmlFor='separator' className='font-bold d-block mb-2'>
            Separator <span className='text-danger'>*</span>
          </label>
          <Dropdown
            id='separator'
            value={separator}
            onChange={(e) => setSeparator(e.value)}
            options={separatorOptions}
            optionLabel='name'
            placeholder='Select a Separator'
            className='w-100'
          />
        </div>
      )}
      <div className='d-flex justify-content-end'>
        <Button
          label='Detect Columns'
          icon='pi pi-search'
          onClick={handleDetectColumns}
          disabled={!file || !manufacturer || detecting || (file.type === 'text/plain' && !separator)}
          loading={detecting}
        />
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div>
      <h4 className='mb-4'>Step 2: Column Detection</h4>
      <Card className='mb-4'>
        <div className='mb-3'>
          <strong>Detected Columns:</strong> {detectedColumns.length}
        </div>
        <div className='table-responsive'>
          <table className='table table-bordered table-sm'>
            <thead>
              <tr>
                <th>Column #</th>
                <th>Column Name</th>
                <th>Suggested Type</th>
                <th>Sample Values</th>
              </tr>
            </thead>
            <tbody>
              {detectedColumns.map((col, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{col.name}</td>
                  <td>
                    {col.suggestedType && (
                      <span className='badge bg-info'>
                        {FIELD_DEFINITIONS[col.suggestedType]?.label || col.suggestedType}
                      </span>
                    )}
                  </td>
                  <td>
                    {col.sampleValues && col.sampleValues.length > 0
                      ? col.sampleValues.join(', ')
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className='d-flex justify-content-between'>
        <Button
          label='Back'
          icon='pi pi-arrow-left'
          onClick={() => setCurrentStep(1)}
          className='p-button-secondary'
        />
        <Button
          label='Continue to Mapping'
          icon='pi pi-arrow-right'
          iconPos='right'
          onClick={() => setCurrentStep(3)}
        />
      </div>
    </div>
  )

  const renderStep3 = () => {
    // Filter fields based on selected mode
    const visibleFields = Object.keys(FIELD_DEFINITIONS).filter(fieldKey => {
      const field = FIELD_DEFINITIONS[fieldKey]
      if (priceFormatMode === 'separate') {
        // In separate mode, show province price fields and hide price/province
        return field.showInSeparateColumnsMode !== false && fieldKey !== 'price' && fieldKey !== 'province'
      } else {
        // In single mode, show price/province and hide province price fields
        return field.showInSingleColumnMode !== false && !fieldKey.startsWith('price_')
      }
    })

    return (
      <div>
        <h4 className='mb-4'>Step 3: Map Columns</h4>
        <p className='text-muted mb-4'>
          Map each detected column to the corresponding field. Fields marked with{' '}
          <span className='text-danger'>*</span> are required.
        </p>
        
        {/* Price Format Mode Selection */}
        <div className='mb-4'>
          <label htmlFor='priceFormatMode' className='font-bold d-block mb-2'>
            Price Format <span className='text-danger'>*</span>
          </label>
          <Dropdown
            id='priceFormatMode'
            value={priceFormatMode}
            onChange={(e) => {
              setPriceFormatMode(e.value)
              // Clear price-related mappings when switching modes
              const newMapping = { ...columnMapping }
              if (e.value === 'separate') {
                delete newMapping.price
                delete newMapping.province
              } else {
                // Remove all province price mappings
                Object.keys(newMapping).forEach(key => {
                  if (key.startsWith('price_')) {
                    delete newMapping[key]
                  }
                })
              }
              setColumnMapping(newMapping)
            }}
            options={[
              { label: 'Single Column (Price + Province)', value: 'single' },
              { label: 'Separate Columns (One per Province)', value: 'separate' }
            ]}
            className='w-100'
          />
          <small className='text-muted d-block mt-1'>
            {priceFormatMode === 'single' 
              ? 'Use a single price column and province column (one row per province)'
              : 'Use separate price columns for each province (one row with all provinces)'}
          </small>
        </div>

        <div className='row'>
          {visibleFields.map((fieldKey) => {
            const field = FIELD_DEFINITIONS[fieldKey]
            // Check if this field was auto-mapped
            const isAutoMapped = detectedColumns.some(col => 
              col.suggestedType === fieldKey && 
              columnMapping[fieldKey] === col.index &&
              col.confidence > 0.5
            )
            
            return (
              <div key={fieldKey} className='col-md-6 mb-3'>
                <label htmlFor={fieldKey} className='font-bold d-block mb-2'>
                  {field.label}
                  {field.required && <span className='text-danger ms-1'>*</span>}
                  {!field.required && <span className='text-muted ms-1'>(Optional)</span>}
                  {isAutoMapped && (
                    <span className='badge bg-success ms-2' title='Auto-detected'>
                      <i className='pi pi-check' style={{ fontSize: '0.8rem' }}></i> Auto
                    </span>
                  )}
                </label>
                <Dropdown
                  id={fieldKey}
                  value={columnMapping[fieldKey] !== undefined ? columnMapping[fieldKey] : 'none'}
                  onChange={(e) => handleColumnMappingChange(fieldKey, e.value)}
                  options={getColumnOptions()}
                  placeholder='Select a column'
                  className='w-100'
                />
                <small className='text-muted d-block mt-1'>{field.description}</small>
              </div>
            )
          })}
        </div>

        <Card className='mt-3'>
          <div className='d-flex align-items-start gap-2'>
            <Checkbox
              inputId='discontinueMissing'
              checked={discontinueMissing}
              onChange={(e) => setDiscontinueMissing(!!e.checked)}
            />
            <div>
              <label htmlFor='discontinueMissing' className='font-bold d-block mb-1'>
                Discontinue products not included in this upload (recommended)
              </label>
              <small className='text-muted d-block'>
                When enabled, any existing products for the selected manufacturer that are not present in this file will be marked as discontinued.
              </small>
            </div>
          </div>
        </Card>

        <div className='d-flex justify-content-between mt-4'>
          <Button
            label='Back'
            icon='pi pi-arrow-left'
            onClick={() => setCurrentStep(2)}
            className='p-button-secondary'
          />
          <Button
            label='Import Catalogue'
            icon='pi pi-upload'
            iconPos='right'
            onClick={handleSubmit}
            loading={loading}
            disabled={!columnMapping.upc && columnMapping.upc !== 0}
          />
        </div>
      </div>
    )
  }

  const getProgressValue = () => {
    return Math.round((currentStep / 3) * 100)
  }

  const getStepStatus = (step) => {
    if (currentStep > step) return 'completed'
    if (currentStep === step) return 'active'
    return 'pending'
  }

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <MainContentLayout title='Catalogue Import Wizard'>
        <Card>
          <div className='mb-4'>
            <div className='d-flex align-items-center mb-3'>
              <div className='flex-grow-1 me-3'>
                <ProgressBar 
                  value={getProgressValue()} 
                  showValue={false}
                  style={{ height: '8px' }}
                />
              </div>
              <span className='text-muted font-bold' style={{ minWidth: '50px', textAlign: 'right' }}>
                {getProgressValue()}%
              </span>
            </div>
            <div className='d-flex justify-content-between position-relative'>
              {/* Progress line */}
              <div 
                className='position-absolute' 
                style={{
                  top: '15px',
                  left: '20px',
                  right: '20px',
                  height: '2px',
                  backgroundColor: '#e9ecef',
                  zIndex: 0
                }}
              />
              <div 
                className='position-absolute' 
                style={{
                  top: '15px',
                  left: '20px',
                  width: `${((currentStep - 1) / 2) * 100}%`,
                  height: '2px',
                  backgroundColor: '#3B82F6',
                  zIndex: 1,
                  transition: 'width 0.3s ease'
                }}
              />
              
              {/* Step indicators */}
              {[1, 2, 3].map((step) => {
                const status = getStepStatus(step)
                const stepLabels = ['Upload File', 'Detect Columns', 'Map Columns']
                return (
                  <div key={step} className='d-flex flex-column align-items-center' style={{ flex: 1, position: 'relative', zIndex: 2 }}>
                    <div
                      className={`rounded-circle d-flex align-items-center justify-content-center ${
                        status === 'completed'
                          ? 'bg-success text-white'
                          : status === 'active'
                          ? 'bg-primary text-white'
                          : 'bg-light text-muted border'
                      }`}
                      style={{
                        width: '32px',
                        height: '32px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {status === 'completed' ? (
                        <i className='pi pi-check' style={{ fontSize: '14px' }}></i>
                      ) : (
                        step
                      )}
                    </div>
                    <span
                      className={`mt-2 text-center small ${
                        status === 'active' ? 'font-bold text-primary' : status === 'completed' ? 'text-success' : 'text-muted'
                      }`}
                      style={{ fontSize: '12px', maxWidth: '100px' }}
                    >
                      {stepLabels[step - 1]}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </Card>
      </MainContentLayout>
    </>
  )
}

